import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shuffle, ChevronsDown } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CipherMapping } from "@/lib/types/cipher";

interface SelectedEmoji {
  char: string;
  code: string;
}

interface KeyboardAssignmentProps {
  characters: string[];
  emojis: SelectedEmoji[];
  onMappingChange: (mapping: CipherMapping) => void;
  onValidationChange: (isValid: boolean) => void;
}

// Define keyboard layouts for different character sets
const QWERTY_LAYOUT = {
  uppercase: [
    ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
    ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
    ["Z", "X", "C", "V", "B", "N", "M"]
  ],
  lowercase: [
    ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
    ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
    ["z", "x", "c", "v", "b", "n", "m"]
  ],
  numbers: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  symbols: [
    [".", ",", "!", "?", "@", "#", "$", "%", "&", "*"],
    ["(", ")", "-", "_", "+", "=", "/", "\\", "<", ">"],
    ["|", "[", "]", "{", "}", "^", "~", "`", " "]
  ]
};

export default function KeyboardAssignment({
  characters = [],
  emojis = [],
  onMappingChange,
  onValidationChange
}: KeyboardAssignmentProps) {
  const [error, setError] = useState<string>("");

  // Create initial mapping
  const [mapping, setMapping] = useState<CipherMapping>(() => {
    const initialMapping: CipherMapping = {
      forward: {},
      reverse: {}
    };
    
    characters.forEach((char, index) => {
      const emoji = emojis[index];
      if (!char || !emoji?.char || !emoji?.code) {
        console.warn("Invalid mapping data:", { char, emoji });
        return;
      }
      
      // Forward mapping: sourceChar -> {targetEmoji, emojiCode}
      initialMapping.forward[char] = {
        targetEmoji: emoji.char,
        emojiCode: emoji.code
      };
      
      // Reverse mapping: targetChar -> {sourceChar, emojiCode}
      initialMapping.reverse[emoji.char] = {
        sourceChar: char,
        emojiCode: emoji.code
      };
    });
    return initialMapping;
  });

  useEffect(() => {
    // Validate mapping whenever characters or emojis change
    if (characters.length !== emojis.length) {
      setError("Character and emoji counts do not match");
      return;
    }
    
    // Create initial mapping
    const newMapping: CipherMapping = {
      forward: {},
      reverse: {}
    };

    characters.forEach((char, index) => {
      const emoji = emojis[index];
      if (!emoji?.char || !emoji?.code) {
        console.warn("Invalid mapping data:", { char, emoji });
        return;
      }
      
      // Forward mapping: sourceChar -> {targetEmoji, emojiCode}
      newMapping.forward[char] = {
        targetEmoji: emoji.char,
        emojiCode: emoji.code
      };
      
      // Reverse mapping: targetChar -> {sourceChar, emojiCode}
      newMapping.reverse[emoji.char] = {
        sourceChar: char,
        emojiCode: emoji.code
      };
    });

    console.log("Created mapping in KeyboardAssignment:", newMapping);
    
    // Update parent component
    onMappingChange(newMapping);
    
    // Validate the mapping
    const { isValid, unmappedChars } = validateMapping(newMapping);
    onValidationChange(isValid);
  }, [characters, emojis]);

  // Validation function
  const validateMapping = (mapping: CipherMapping): { isValid: boolean; unmappedChars: string[] } => {
    // Check basic structure
    if (!mapping.forward || !mapping.reverse) {
      console.error("Missing forward or reverse mapping");
      return { isValid: false, unmappedChars: [] };
    }

    // Check mapping counts
    const forwardCount = Object.keys(mapping.forward).length;
    const reverseCount = Object.keys(mapping.reverse).length;
    
    console.log("Mapping validation counts:", { forwardCount, reverseCount });

    // Find any unmapped characters
    const unmappedChars = characters.filter(char => !mapping.forward[char]);

    if (forwardCount !== characters.length || reverseCount !== emojis.length || unmappedChars.length > 0) {
      console.error("Mapping validation failed:", { 
        forwardCount, 
        reverseCount, 
        expectedCount: characters.length,
        unmappedChars 
      });
      return { isValid: false, unmappedChars };
    }

    return { isValid: true, unmappedChars: [] };
  };

  const randomizeMapping = () => {
    // Create a copy of the emojis array and shuffle it
    const availableEmojis = [...emojis]; // Only use the selected emojis
    const shuffledEmojis = availableEmojis
      .sort(() => Math.random() - 0.5)
      .slice(0, characters.length); // Ensure we only take what we need
    
    // Create new mapping
    const newMapping: CipherMapping = {
      forward: {},
      reverse: {},
    };

    characters.forEach((char, index) => {
      const emoji = shuffledEmojis[index];
      if (emoji) {
        // Add to forward mapping
        newMapping.forward[char] = {
          targetEmoji: emoji.char,
          emojiCode: emoji.code
        };
        
        // Add to reverse mapping
        newMapping.reverse[emoji.char] = {
          sourceChar: char,
          emojiCode: emoji.code
        };
      }
    });

    console.log('New randomized mapping structure:', JSON.stringify(newMapping, null, 2));
    
    // Update state and notify parent
    setMapping(newMapping);
    onMappingChange(newMapping);
  };

  // Group characters by type
  const characterGroups = {
    uppercase: characters.filter(char => /[A-Z]/.test(char)),
    lowercase: characters.filter(char => /[a-z]/.test(char)),
    numbers: characters.filter(char => /[0-9]/.test(char)),
    additional: characters.filter(char => {
      // All characters that aren't uppercase, lowercase, or numbers
      return !/[A-Za-z0-9]/.test(char);
    })
  };

  const renderKeyboardRow = (chars: string[], rowIndex: number) => (
    <div key={rowIndex} className="flex flex-wrap justify-start gap-2 mb-2">
      {chars.map((char) => {
        const mappedEmoji = mapping.forward[char];
        return (
          <TooltipProvider key={char}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center">
                  <Card className="w-16 h-16 flex flex-col items-center justify-center p-2 bg-white hover:bg-gray-50 transition-colors">
                    <div className="text-base font-medium mb-1">
                      {char === " " ? "␣" : char}
                    </div>
                    <div className="text-2xl">
                      {mappedEmoji ? mappedEmoji.targetEmoji : "?"}
                    </div>
                  </Card>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Unicode: {mappedEmoji ? mappedEmoji.emojiCode : "Not mapped"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );

  const { isValid, unmappedChars } = validateMapping(mapping);
  const totalCharacters = characters.length;
  const needsScroll = totalCharacters > 62; // More than uppercase + lowercase letters

  return (
    <div className="h-full flex flex-col gap-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500">
          {!isValid && (
            <span className="text-red-500">
              {unmappedChars.length > 0 
                ? `Missing mappings for: ${unmappedChars.join(", ")}`
                : "Invalid emoji assignments detected"}
            </span>
          )}
        </div>
        <Button
          variant="outline"
          onClick={randomizeMapping}
          className="flex items-center gap-2"
        >
          <Shuffle className="h-4 w-4" />
          Randomize Mapping
        </Button>
      </div>

      <div className="flex-1 flex flex-col min-h-0 relative">
        {needsScroll && (
          <div className="flex items-center justify-end gap-1 text-xs text-gray-500 mb-2">
            <ChevronsDown className="h-3 w-3 animate-bounce" />
            <span>Scroll for more</span>
          </div>
        )}
        <div className="flex-1 bg-gray-50 rounded-lg overflow-hidden relative">
          <ScrollArea className="h-full">
            <div className="p-4 space-y-4">
              {/* Uppercase Letters */}
              {characterGroups.uppercase.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Uppercase Letters</h3>
                  {renderKeyboardRow(characterGroups.uppercase, 0)}
                </div>
              )}

              {/* Lowercase Letters */}
              {characterGroups.lowercase.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Lowercase Letters</h3>
                  {renderKeyboardRow(characterGroups.lowercase, 0)}
                </div>
              )}

              {/* Numbers */}
              {characterGroups.numbers.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Numbers</h3>
                  {renderKeyboardRow(characterGroups.numbers, 0)}
                </div>
              )}

              {/* Additional Characters */}
              {characterGroups.additional.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Additional Characters</h3>
                  {renderKeyboardRow(characterGroups.additional, 0)}
                </div>
              )}
            </div>
          </ScrollArea>
          {needsScroll && (
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-gray-50 to-transparent pointer-events-none" />
          )}
        </div>
      </div>
    </div>
  );
} 